import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Button, Input, Popconfirm, Select, Space, Table, Tooltip, message } from 'antd'
import { CopyOutlined, EyeInvisibleOutlined, FilePdfOutlined, MailOutlined, PlusOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'
import CurrentStaffContainer from '../CurrentStaff/CurrentStaffContainer'

import { FileThumbList } from './ContractFiles'
import { openContractPdf } from './pdf'
import {
  DEPARTMENTS,
  EMPLOYEE_CONTRACTS_QUERY,
  HR_ROLES,
  SEND_EMPLOYEE_CONTRACT_ONBOARD_EMAIL_MUTATION,
  formatMutationErrors
} from './graphql'

const PAGE_SIZE = 20
const DISPLAY_FORMAT = 'DD/MM/YYYY'
const SALARY_MASK = '••••'

const formatDate = value => (value ? dayjs(value).format(DISPLAY_FORMAT) : '-')

const EmployeeContractsIndexContainer = ({ rolesMatched, currentStaffLoading }) => {
  const { t } = useTranslation('employeeContracts')
  const isHr = rolesMatched(HR_ROLES)

  const [page, setPage] = useState(1)
  const [query, setQuery] = useState('')
  const [department, setDepartment] = useState(undefined)
  const [revealedSalaryIds, setRevealedSalaryIds] = useState([])
  const [pdfLoadingId, setPdfLoadingId] = useState(null)
  const [emailingId, setEmailingId] = useState(null)

  useEffect(() => {
    document.title = 'Employee Contracts | OSH CRM'
  }, [])

  const { data, loading } = useQuery(EMPLOYEE_CONTRACTS_QUERY, {
    variables: {
      page,
      per: PAGE_SIZE,
      query: query || undefined,
      department
    },
    fetchPolicy: 'cache-and-network'
  })

  const [sendOnboardEmail] = useMutation(SEND_EMPLOYEE_CONTRACT_ONBOARD_EMAIL_MUTATION)

  const result = (data && data.employeeContracts) || {}

  const resetPage = setter => (value) => {
    setter(value)
    setPage(1)
  }

  const toggleSalary = (id) => {
    setRevealedSalaryIds(ids => (ids.includes(id) ? ids.filter(value => value !== id) : ids.concat(id)))
  }

  const handleDownloadPdf = async (contract) => {
    setPdfLoadingId(contract.id)
    try {
      const opened = await openContractPdf(contract.id)
      if (!opened) message.error(t('pdfFailed'))
    } catch (error) {
      message.error(error.message)
    } finally {
      setPdfLoadingId(null)
    }
  }

  const handleSendOnboardEmail = async (contract) => {
    setEmailingId(contract.id)
    try {
      const { data: response } = await sendOnboardEmail({ variables: { id: contract.id } })
      const payload = response && response.sendEmployeeContractOnboardEmail
      if (!payload) { message.error(t('emailFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }
      if (!payload.sent) { message.error(t('emailFailed')); return }

      message.success(t('emailSent'))
    } catch (error) {
      message.error(error.message)
    } finally {
      setEmailingId(null)
    }
  }

  const handleCopyCandidateLink = (contract) => {
    navigator.clipboard.writeText(contract.candidateUploadUrl)
      .then(() => message.success(t('linkCopied')))
      .catch(() => message.error(t('copyFailed')))
  }

  const renderSalary = (contract) => {
    const display = contract.salary && contract.salary.display
    if (!display) return '-'

    if (revealedSalaryIds.includes(contract.id)) {
      return (
        <Button onClick={() => toggleSalary(contract.id)} size='small' type='link'>
          {display} <EyeInvisibleOutlined />
        </Button>
      )
    }

    return (
      <Tooltip title={t('showSalary')}>
        <Button onClick={() => toggleSalary(contract.id)} size='small' type='link'>{SALARY_MASK}</Button>
      </Tooltip>
    )
  }

  const columns = [
    {
      title: t('date'),
      dataIndex: 'date',
      key: 'date',
      render: formatDate
    },
    {
      title: t('idCardNumber'),
      dataIndex: 'idCardNumber',
      key: 'idCardNumber',
      render: value => value || '-'
    },
    {
      title: t('name'),
      key: 'name',
      render: (_value, record) => (
        <div>
          <div>{record.name || '-'}</div>
          {record.position && <small>{record.position}</small>}
        </div>
      )
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      render: value => value || '-'
    },
    {
      title: t('salary'),
      key: 'salary',
      render: (_value, record) => renderSalary(record)
    },
    {
      title: t('department'),
      dataIndex: 'department',
      key: 'department',
      render: value => (value ? t(`departments.${value}`) : '-')
    },
    {
      title: t('signedContracts'),
      key: 'signedContracts',
      render: (_value, record) => <FileThumbList files={record.signedContracts} />
    },
    {
      title: t('documents'),
      key: 'documents',
      render: (_value, record) => <FileThumbList files={record.documents} />
    },
    isHr && {
      title: t('createdBy'),
      key: 'staff',
      render: (_value, record) => (record.staff && record.staff.fullName) || '-'
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_value, record) => (
        <Space wrap>
          <Link to={`/employee_contracts/${record.id}/edit`}>{t('edit')}</Link>
          <Button
            icon={<FilePdfOutlined />}
            loading={pdfLoadingId === record.id}
            onClick={() => handleDownloadPdf(record)}
            size='small'
          >
            {t('downloadPdf')}
          </Button>
          <Popconfirm
            cancelText={t('cancel')}
            okText={t('send')}
            onConfirm={() => handleSendOnboardEmail(record)}
            title={t('sendOnboardEmailConfirm', { email: record.email || '-' })}
          >
            <Button icon={<MailOutlined />} loading={emailingId === record.id} size='small'>
              {t('sendOnboardEmail')}
            </Button>
          </Popconfirm>
          <Button
            disabled={!record.candidateUploadUrl}
            icon={<CopyOutlined />}
            onClick={() => handleCopyCandidateLink(record)}
            size='small'
          >
            {t('copyCandidateLink')}
          </Button>
        </Space>
      )
    }
  ].filter(Boolean)

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/employee_contracts', name: t('title') }
      ]}>
        <ContentLayout>
          <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
            <Space wrap>
              <Input.Search
                allowClear
                onSearch={resetPage(value => setQuery(value.trim()))}
                placeholder={t('searchPlaceholder')}
                style={{ width: 260 }}
              />
              <Select
                allowClear
                onChange={resetPage(setDepartment)}
                placeholder={t('allDepartments')}
                style={{ width: 180 }}
                value={department}
              >
                {DEPARTMENTS.map(value => (
                  <Select.Option key={value} value={value}>{t(`departments.${value}`)}</Select.Option>
                ))}
              </Select>
            </Space>
            <Link to='/employee_contracts/new'>
              <Button icon={<PlusOutlined />} type='primary'>{t('new')}</Button>
            </Link>
          </Space>

          <Table
            columns={columns}
            dataSource={result.employeeContracts || []}
            loading={loading || currentStaffLoading}
            onChange={pagination => setPage(pagination.current)}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              total: result.totalCount || 0
            }}
            rowKey='id'
            scroll={{ x: true }}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

EmployeeContractsIndexContainer.propTypes = {
  currentStaffLoading: PropTypes.bool,
  rolesMatched: PropTypes.func.isRequired
}

EmployeeContractsIndexContainer.defaultProps = {
  currentStaffLoading: false
}

export default CurrentStaffContainer(EmployeeContractsIndexContainer)
